import React, { useEffect, useState } from "react";
import { Download, CheckCircle2, Clock, FileText, ExternalLink } from "lucide-react";
import { api, API } from "../../lib/api";
import { trackClick } from "../../lib/crossSell";

/**
 * Toolkit Downloads section displayed on the post-purchase Wealth Results page.
 * Renders the full deliverable roadmap (#1 Available + #2-6 Coming Soon).
 * Download buttons hit the gated /api/wealth/toolkit/download endpoint which
 * also logs every download to the admin dashboard.
 */
export default function ToolkitDownloads({ leadId }) {
    const [deliverables, setDeliverables] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!leadId) return;
        let alive = true;
        (async () => {
            try {
                const { data } = await api.get(`/wealth/toolkit/deliverables/${leadId}`);
                if (alive) setDeliverables(data.deliverables);
            } catch (e) {
                if (alive) setError(e?.response?.data?.detail || "Could not load Toolkit downloads.");
            }
        })();
        return () => { alive = false; };
    }, [leadId]);

    if (!leadId) return null;
    if (error) {
        return (
            <div className="mt-8 p-6 rounded-2xl bg-rose-50 border border-rose-200 text-rose-900 text-sm" data-testid="toolkit-downloads-error">
                {error}
            </div>
        );
    }
    if (!deliverables) {
        return (
            <div className="mt-8 p-6 rounded-2xl bg-white border border-emerald-900/10 text-sm text-emerald-900" data-testid="toolkit-downloads-loading">
                Loading your Toolkit downloads…
            </div>
        );
    }

    return (
        <div
            className="mt-8 p-8 sm:p-10 rounded-2xl bg-white border border-emerald-900/10 shadow-xl shadow-emerald-900/5"
            data-testid="toolkit-downloads"
        >
            <div className="flex items-start justify-between flex-wrap gap-3 mb-2">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.22em] text-amber-700 font-bold">Your Toolkit Downloads</div>
                    <h3 className="mt-2 font-serif text-2xl sm:text-3xl text-emerald-950 font-semibold leading-tight">
                        Retirement Readiness Toolkit&trade;
                    </h3>
                </div>
                <div className="text-xs text-emerald-900 max-w-sm">
                    We release your Toolkit one deliverable at a time. Coming-soon guides unlock automatically — no action needed.
                </div>
            </div>

            <ul className="mt-6 divide-y divide-emerald-900/5" data-testid="deliverables-list">
                {deliverables.map((d) => {
                    const isAvailable = d.status === "available";
                    const downloadUrl = isAvailable
                        ? `${API}/wealth/toolkit/download/${leadId}/${d.id}`
                        : null;
                    return (
                        <li
                            key={d.id}
                            className="py-5 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4"
                            data-testid={`deliverable-${d.id}`}
                        >
                            <div className="flex gap-4 flex-1 min-w-0">
                                <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center font-serif font-bold ${isAvailable ? "bg-emerald-900 text-amber-300" : "bg-emerald-900/8 text-emerald-800"}`}>
                                    {d.number}
                                </div>
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-semibold text-emerald-950 text-base">{d.title}</span>
                                        {isAvailable ? (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-bold tracking-wide uppercase" data-testid={`status-${d.id}`}>
                                                <CheckCircle2 className="w-3 h-3" /> Available
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 text-[10px] font-bold tracking-wide uppercase" data-testid={`status-${d.id}`}>
                                                <Clock className="w-3 h-3" /> Coming Soon
                                            </span>
                                        )}
                                    </div>
                                    <p className="mt-1.5 text-sm text-emerald-900 leading-relaxed">{d.description}</p>
                                    <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-emerald-800 font-medium">
                                        <FileText className="w-3 h-3" /> {d.format} download
                                    </div>
                                    {isAvailable && d.affiliate_url && (
                                        <a
                                            href={d.affiliate_url}
                                            target="_blank"
                                            rel="noopener noreferrer sponsored"
                                            onClick={() => trackClick("augusta", { source_page: "wealth_results", lead_id: leadId, deliverable_id: d.id })}
                                            className="mt-3 inline-flex items-center gap-1.5 text-[12px] font-semibold text-amber-700 hover:text-amber-900 underline underline-offset-2 decoration-amber-300 hover:decoration-amber-700 transition-colors"
                                            data-testid={`affiliate-${d.id}`}
                                        >
                                            <ExternalLink className="w-3 h-3" />
                                            {d.affiliate_label || "Learn More About Gold and Silver IRAs"}
                                        </a>
                                    )}
                                </div>
                            </div>
                            <div className="sm:self-center flex-shrink-0">
                                {isAvailable ? (
                                    <a
                                        href={downloadUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-2 bg-emerald-900 hover:bg-emerald-800 text-amber-300 px-4 py-2.5 rounded-lg font-semibold text-sm shadow-md transition-colors"
                                        data-testid={`download-${d.id}`}
                                    >
                                        <Download className="w-4 h-4" /> Download
                                    </a>
                                ) : (
                                    <span className="inline-flex items-center gap-2 bg-emerald-900/5 text-emerald-800 px-4 py-2.5 rounded-lg font-semibold text-sm cursor-not-allowed">
                                        <Clock className="w-4 h-4" /> Coming Soon
                                    </span>
                                )}
                            </div>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}
